import sop from "@/images/icons/sop.svg";
import "./SopTemplate.module.scss";

import {boundMethod} from "autobind-decorator";
import {AnimatePresence, motion} from "framer-motion";
import {debounce, floor} from "lodash";
import React from "react";
import {IntlShape, injectIntl} from "react-intl";
import {HtmlPortalNode, InPortal} from "react-reverse-portal";
import {Link} from "react-router-dom";

import {ITemplateModel, ITemplateResponse} from "@/components/template/models";
import http from "@/services/http";
import {ILicenses} from "@/services/models";
import {IPaging, pagingDefault} from "@toolbox/building-blocks/models";
import {animation, onClosed, onOpen} from "@toolbox/design/models";
import {EDeviceClasses, IDLE_DELAY} from "@toolbox/models";
import {ETechnology, IDeviceClassGroup, IDeviceClassItemGroup} from "./models";

import PagerPageSizeFooter from "@toolbox/building-blocks/PagerPageSizeFooter";
import ModuleIcon from "@toolbox/display-blocks/ModuleIcon";
import SearchSuggestions from "@toolbox/display-blocks/SearchSuggestion";
import {
    device2DeviceApi,
    getDeviceDisplayName,
} from "@toolbox/functions/device-check";
import SearchInput from "@toolbox/nativ-inputs/SearchInput";
import T from "@translate/T";

function sortTemp(templates: ITemplateModel[]): IDeviceClassItemGroup[] {
    const groups: IDeviceClassGroup[] = [
        {
            id: device2DeviceApi(EDeviceClasses.Fracture),
            header: getDeviceDisplayName(EDeviceClasses.Fracture),
        },
        {
            id: device2DeviceApi(EDeviceClasses.Fuge),
            header: getDeviceDisplayName(EDeviceClasses.Fuge),
        },
        {
            id: device2DeviceApi(EDeviceClasses.Reader),
            header: getDeviceDisplayName(EDeviceClasses.Reader),
        },
        {
            id: device2DeviceApi(EDeviceClasses.XRay),
            header: getDeviceDisplayName(EDeviceClasses.XRay),
        },
        {
            id: device2DeviceApi(EDeviceClasses.Sizer),
            header: getDeviceDisplayName(EDeviceClasses.Sizer),
        },
        {
            id: device2DeviceApi(EDeviceClasses.Spoc),
            header: getDeviceDisplayName(EDeviceClasses.Spoc),
        },
    ];

    const itemGroups: IDeviceClassItemGroup[] = groups.map(({id, header}) => {
        return {items: [], id, header};
    });

    for (const template of templates) {
        for (const itemGroup of itemGroups) {
            if (itemGroup.id === device2DeviceApi(template.deviceClass)) {
                itemGroup.items.push(template);
            }
        }
    }

    return itemGroups.filter((x) => x.items.length > 0);
}

interface ISopTemplateProps {
    intl: IntlShape;
    project: number;
    licenses: ILicenses;

    columns: number;
    portal: HtmlPortalNode<PagerPageSizeFooter>;

    hrefTarget?: string;

    onClick(): void;
}

interface ISopTemplateState extends IPaging {
    templates: ITemplateModel[];
    suggestions: string[];

    query: string;
    emptyResult: boolean;
}

class SopTemplate extends React.PureComponent<
    ISopTemplateProps,
    ISopTemplateState
> {
    public readonly state: ISopTemplateState = {
        ...pagingDefault,
        query: "",
        templates: [],
        suggestions: [],
        emptyResult: false,
    };

    private readonly trigger = debounce(this.refresh, IDLE_DELAY);

    private get columns() {
        return Math.max(1, floor(this.props.columns / 2));
    }

    public async componentDidMount() {
        await this.trigger();
    }

    public componentWillUnmount() {
        this.trigger.cancel();
    }

    @boundMethod
    public setQuery(query: string) {
        this.setState({query, page: 1}, this.refresh);
    }

    @boundMethod
    public setPage(page: number) {
        this.setState({page}, this.refresh);
    }

    @boundMethod
    public setPageSize(pageSize: number) {
        this.setState({pageSize, page: 1}, this.refresh);
    }

    public render() {
        const {suggestions, query} = this.state;

        return (
            <React.Fragment>
                <SearchInput query={query} onSearch={this.setQuery} />
                <SearchSuggestions
                    suggestions={suggestions}
                    onClick={this.setQuery}
                />

                {this.renderContent()}
            </React.Fragment>
        );
    }

    private renderContent() {
        const {emptyResult} = this.state;

        if (emptyResult) {
            return (
                <p className="text-center text-warning">
                    <T>Your search did not match any templates.</T>
                </p>
            );
        }

        return (
            <div className="div-overflow-y" styleName="list-template">
                {this.renderResults()}
            </div>
        );
    }

    private renderResults() {
        const {templates} = this.state;

        const items = sortTemp(templates);
        const last = items.length - 1;

        return items.map((template, i) => (
            <div key={template.id} className={last !== i ? "mb-2" : undefined}>
                <h5 className="header border-bottom">{template.header}</h5>
                <ul className="list-unstyled" style={{columns: this.columns}}>
                    <AnimatePresence exitBeforeEnter={true}>
                        {this.renderButtons(template, i)}
                    </AnimatePresence>
                </ul>
            </div>
        ));
    }

    private renderButtons(template: IDeviceClassItemGroup, i: number) {
        const columns = this.columns;
        const last = floor(template.items.length - 1 / columns);

        return template.items.map((item) =>
            this.renderButton(item, last === floor(i / columns), i),
        );
    }

    private renderButton(item: ITemplateModel, lastRow: boolean, i: number) {
        const {hrefTarget, onClick} = this.props;

        return (
            <motion.li
                key={item.id}
                className={!lastRow ? "mb-1" : undefined}
                animate={onOpen}
                exit={onClosed}
                initial={onClosed}
                transition={animation}
            >
                <Link
                    id={`link-to-${i}`}
                    target={hrefTarget}
                    to={this.getUrl(
                        item.id,
                        item.technology ?? ETechnology.Step,
                        item.deviceClass ?? EDeviceClasses.Reader,
                    )}
                    onClick={onClick}
                >
                    <ModuleIcon icon={sop} name={item.name} />
                </Link>
            </motion.li>
        );
    }

    // private renderFooter() {
    //     const {portal} = this.props;
    //     const {total, pageSize, page} = this.state;

    //     return (
    //         <InPortal node={portal}>
    //             <PagerPageSizeFooter
    //                 page={page}
    //                 total={total}
    //                 pageSize={pageSize}
    //                 setPage={this.setPage}
    //                 setPageSize={this.setPageSize}
    //             />
    //         </InPortal>
    //     );
    // }

    @boundMethod
    private async refresh() {
        const {page, query, pageSize} = this.state;

        try {
            const response = await http
                .get(`/api/sop/template/all`, {
                    searchParams: {
                        asc: false,
                        page: page.toString(),
                        pageSize,
                        query,
                        sort: "saved",
                    },
                })
                .json<ITemplateResponse>();

            this.setState({
                emptyResult: !response.items.length,
                page: response.page,
                templates: response.items,
                total: response.total,
            });
        } catch {
            this.setState({...pagingDefault, templates: [], emptyResult: true});
        }
    }

    private getUrl(id: number, technology: string, device?: EDeviceClasses) {
        const {project} = this.props;
        const baseUrl = `/project/${project}/${technology}/wizard`;

        if (technology === ETechnology.Step) {
            return `${baseUrl}/${device2DeviceApi(device)}?template=${id}&`;
        }

        return `${baseUrl}?template=${id}&`;
    }
}

export default injectIntl(SopTemplate);
